import Button from "../../../../components/ui/Button";
import Icon from "../../../../components/AppIcon";
import BillPrintView from "../Bills";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import axiosInstance from "../../../../utils/axiosInstance";

const getPaymentMethodIcon = (method) => {
  const mode = method || "CASH";
  switch (mode.toUpperCase()) {
    case "CARD":
      return "CreditCard";
    case "UPI":
      return "Smartphone";
    case "NETBANKING":
      return "Globe";
    case "WALLET":
      return "Wallet";
    case "CHEQUE":
      return "FileText";
    case "ONLINE":
      return "Monitor";
    default:
      return "DollarSign";
  }
};

const getPaymentModeColor = (mode) => {
  switch (mode?.toUpperCase()) {
    case "CASH":
      return "bg-green-100 text-green-800 border-green-200";
    case "CARD":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "UPI":
      return "bg-purple-100 text-purple-800 border-purple-200";
    case "ONLINE":
      return "bg-cyan-100 text-cyan-800 border-cyan-200";
    case "CHEQUE":
      return "bg-amber-100 text-amber-800 border-amber-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);

const PaymentTab = ({ order, payments, onOpenModal }) => {
  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const totalAmount = order.finalAmount || 0;
  const [sendingLink, setSendingLink] = useState(false);
  const balance = totalAmount - totalPaid;

  const paymentStats = [
    {
      label: "Total Amount",
      value: totalAmount,
      description: "Order value after all adjustments",
      color: "border-l-blue-500",
    },
    {
      label: "Amount Paid",
      value: totalPaid,
      description: `Through ${payments.length} payment${
        payments.length !== 1 ? "s" : ""
      }`,
      color: "border-l-green-500",
    },
    {
      label: "Balance Due",
      value: balance,
      description: balance > 0 ? "Pending payment" : "Payment complete",
      color: balance > 0 ? "border-l-amber-500" : "border-l-emerald-500",
    },
  ];

  console.log("payments--",payments)
  return (
    <div className="space-y-4 p-3">
      {/* Payment Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {paymentStats.map((stat, index) => (
          <div
            key={index}
            className={`bg-white border border-gray-200 rounded-lg overflow-hidden ${stat.color}`}
            style={{ borderLeftWidth: "3px" }}>
            <div className="p-3">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-xs text-gray-500 font-medium">
                    {stat.label}
                  </p>
                  <p className="text-lg font-bold text-gray-900 mt-0.5">
                    {formatCurrency(stat.value)}
                  </p>
                </div>
                {index === 2 && (
                  <span
                    className={`text-[10px] font-medium px-2 py-1 rounded-full ${
                      balance > 0
                        ? "bg-amber-100 text-amber-800"
                        : "bg-emerald-100 text-emerald-800"
                    }`}>
                    {balance > 0 ? "PENDING" : "PAID"}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400">{stat.description}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            Payment History
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {payments.length} payment{payments.length !== 1 ? "s" : ""} recorded
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* NEW BUTTON — Send Payment Link */}
          {balance > 0 && (
            <Button
              disabled={sendingLink}
              onClick={async () => {
                try {
                  setSendingLink(true);

                  const res = await axiosInstance.get(
                    `${
                      import.meta.env.VITE_API_BASE_URL
                    }/api/pg/razorpay/link?orderId=${
                      order.id
                    }&patientId=${order?.patientId}`,
                  );
                } catch (err) {
                  console.error("Generate link error:", err);

                  if (err.response) {
                    alert(
                      err.response.data?.message ||
                        `Request failed (${err.response.status})`,
                    );
                  } else {
                    alert(err.message || "Network/CORS error");
                  }
                } finally {
                  setSendingLink(false);
                }
              }}
              className={`bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 h-8 ${
                sendingLink ? "opacity-80 cursor-not-allowed" : ""
              }`}>
              {sendingLink ? (
                <span className="flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Sending...
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <Icon name="Send" size={12} className="mr-1.5" />
                  Send Payment Link
                </span>
              )}
            </Button>
          )}

          <Button
            onClick={onOpenModal}
            className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1.5 h-8">
            <Icon name="Plus" size={12} className="mr-1.5" />
            Record Payment
          </Button>
        </div>
      </div>

      {/* Payment History Table */}
      {payments.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
          <Icon
            name="CreditCard"
            size={24}
            className="mx-auto mb-3 text-gray-300"
          />
          <p className="text-xs text-gray-500 font-medium">
            No payments recorded
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Click "Record Payment" to add the first payment
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-3 py-2.5 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500">
            <div className="col-span-3">Payment Details</div>
            <div className="col-span-2">Method</div>
            <div className="col-span-3">Transaction Info</div>
            <div className="col-span-2">Date & Time</div>
            <div className="col-span-2 text-right">Amount</div>
          </div>

          {/* Table Rows */}
          <div className="divide-y divide-gray-100">
            {payments.map((payment) => (
              <div
                key={payment.id}
                className="grid grid-cols-12 gap-4 px-3 py-2.5 hover:bg-gray-50/50 transition-colors items-center">
                {/* Payment Details */}
                <div className="col-span-3">
                  <div className="flex items-center gap-2">
                    <div
                      className={`p-1.5 rounded border ${getPaymentModeColor(
                        payment.paymentMethod,
                      )}`}>
                      <Icon
                        name={getPaymentMethodIcon(payment.paymentMethod)}
                        size={12}
                      />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-900">
                        Payment #{payment.referenceNumber || payment.id}
                      </p>
                      {payment.notes && (
                        <p className="text-xs text-gray-500 truncate max-w-[180px]">
                          {payment.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Method */}
                <div className="col-span-2">
                  <span
                    className={`text-xs font-medium px-2 py-1 rounded ${getPaymentModeColor(
                      payment.paymentMethod,
                    )}`}>
                    {payment.paymentMethod?.replace("_", " ") || "Cash"}
                  </span>
                </div>

                {/* Transaction Info */}
                <div className="col-span-3">
                  <div className="space-y-0.5">
                    {payment.transactionId && (
                      <div className="flex items-center gap-1">
                        <p className="text-xs text-gray-500">ID:</p>
                        <p className="text-xs font-medium text-gray-900 font-mono">
                          {payment.transactionId.slice(0, 8)}...
                        </p>
                        <button
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                          onClick={() =>
                            navigator.clipboard.writeText(payment.transactionId)
                          }>
                          <Icon name="Copy" size={10} />
                        </button>
                      </div>
                    )}
                    {payment.bankName && (
                      <p className="text-xs text-gray-500">
                        {payment.bankName}
                      </p>
                    )}
                  </div>
                </div>

                {/* Date & Time */}
                <div className="col-span-2">
                  <p className="text-xs font-medium text-gray-900">
                    {new Date(payment.createdAt).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                    })}
                  </p>
                  <p className="text-xs text-gray-500">
                    {new Date(payment.createdAt).toLocaleTimeString("en-IN", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    })}
                  </p>
                </div>

                {/* Amount */}
                <div className="col-span-2 text-right">
                  <p className="text-sm font-bold text-gray-900">
                    {formatCurrency(payment.amount)}
                  </p>
                  <p className="text-xs text-gray-500">
                    by {payment.collectedBy || "System"}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Footer Summary */}
          <div className="bg-gray-50 border-t border-gray-200 px-3 py-2.5">
            <div className="flex justify-between items-center">
              <div className="text-xs text-gray-500">
                Showing {payments.length} payment
                {payments.length !== 1 ? "s" : ""}
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-xs text-gray-500">Total Paid</p>
                  <p className="text-sm font-bold text-green-700">
                    {formatCurrency(totalPaid)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">Balance</p>
                  <p
                    className={`text-sm font-bold ${
                      balance > 0 ? "text-amber-700" : "text-emerald-700"
                    }`}>
                    {formatCurrency(balance)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {(() => {
        const invoicePayment = payments.find((p) => p.invoiceUrl);
        return invoicePayment ? (
          <Button className="bg-gray-700 hover:bg-gray-800 text-white text-xs px-3 py-1.5 h-8">
            <a
              href={invoicePayment.invoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center">
              <Icon name="Printer" size={12} className="mr-1.5" />
              Invoice
            </a>
          </Button>
        ) : balance <= 0 ? (
          <p className="text-xs text-gray-400">Invoice generating...</p>
        ) : null;
      })()}

      {/* Payment Insights */}
      {payments.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-gray-900 mb-2">
              Payment Methods
            </h4>
            <div className="space-y-2">
              {Array.from(new Set(payments.map((p) => p.paymentMode)))
                .map((mode) => {
                  const count = payments.filter(
                    (p) => p.paymentMode === mode,
                  ).length;
                  const total = payments
                    .filter((p) => p.paymentMode === mode)
                    .reduce((sum, p) => sum + p.amount, 0);
                  return { mode, count, total };
                })
                .map(({ mode, count, total }) => (
                  <div
                    key={mode}
                    className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <Icon
                        name={getPaymentMethodIcon(mode)}
                        size={12}
                        className="text-gray-400"
                      />
                      <span className="font-medium text-gray-700">
                        {mode || "Cash"}
                      </span>
                      <span className="text-gray-400">({count})</span>
                    </div>
                    <span className="font-medium text-gray-900">
                      {formatCurrency(total)}
                    </span>
                  </div>
                ))}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-gray-900 mb-2">
              Payment Timeline
            </h4>
            <div className="text-xs text-gray-600">
              <div className="flex items-center justify-between mb-1">
                <span>First Payment</span>
                <span className="font-medium text-gray-900">
                  {payments.length > 0
                    ? new Date(
                        payments[payments.length - 1].createdAt,
                      ).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                      })
                    : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between mb-1">
                <span>Latest Payment</span>
                <span className="font-medium text-gray-900">
                  {payments.length > 0
                    ? new Date(payments[0].createdAt).toLocaleDateString(
                        "en-IN",
                        {
                          day: "numeric",
                          month: "short",
                        },
                      )
                    : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Payment Frequency</span>
                <span className="font-medium text-gray-900">
                  {payments.length} payment{payments.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      <BillPrintView order={order} payments={payments} />
    </div>
  );
};

export default PaymentTab;
